import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { api } from '../services/api';
import './QRScanner.css';

const QRScanner = ({ onBack }) => {
  const [validationResult, setValidationResult] = useState(null);
  const [scanning, setScanning] = useState(true);
  const [manualCode, setManualCode] = useState('');

  useEffect(() => {
    if (scanning) {
      const scanner = new Html5QrcodeScanner('qr-reader', {
        qrbox: {
          width: 250,
          height: 250,
        },
        fps: 5,
      });

      scanner.render(onScanSuccess, onScanError);

      function onScanSuccess(decodedText, decodedResult) {
        validateTicket(decodedText);
        scanner.clear();
        setScanning(false);
      }

      function onScanError(errorMessage) {
        // QR kod bulunamadığında sürekli hata göstermesin
        // console.warn(errorMessage);
      }

      return () => {
        scanner.clear().catch(error => {
          console.error("Scanner temizleme hatası:", error);
        });
      };
    }
  }, [scanning]);

  const validateTicket = async (qrData) => {
    try {
      const result = await api.tickets.validate(qrData);
      setValidationResult(result);
    } catch (error) {
      console.error('Doğrulama hatası:', error);
      setValidationResult({
        isValid: false,
        message: 'Doğrulama sırasında bir hata oluştu'
      });
    }
  };

  const handleUseTicket = async () => {
    if (!validationResult?.ticket?.id) return;

    try {
      await api.tickets.use(validationResult.ticket.id);
      alert('✅ Bilet kullanıldı olarak işaretlendi!');
      handleReset();
    } catch (error) {
      console.error('Bilet kullanma hatası:', error);
      alert('❌ Bilet kullanılamadı!');
    }
  };

  const handleReset = () => {
    setValidationResult(null);
    setScanning(true);
  };

  const handleManualValidation = () => {
    if (manualCode.trim()) {
      validateTicket(manualCode);
      setManualCode('');
    }
  };

  return (
    <div className="qr-scanner-container">
      <div className="scanner-header">
        <button onClick={onBack} className="back-btn">← Geri</button>
        <h2>📱 QR Kod Okuyucu</h2>
      </div>

      {scanning && !validationResult && (
        <div className="scanner-section">
          <div className="scanner-instructions">
            <h3>📷 Kamerayı Biletteki QR Koda Tutun</h3>
            <p>Bilet otomatik olarak taranacak ve doğrulanacaktır</p>
          </div>

          <div id="qr-reader" className="qr-reader"></div>

          <div className="manual-entry">
            <h4>Manuel Giriş</h4>
            <p>Kamera çalışmıyorsa bilet kodunu elle girebilirsiniz:</p>
            <div className="manual-input-group">
              <input
                type="text"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Bilet kodunu girin..."
                className="manual-input"
                onKeyPress={(e) => e.key === 'Enter' && handleManualValidation()}
              />
              <button onClick={handleManualValidation} className="manual-btn">
                Doğrula
              </button>
            </div>
          </div>
        </div>
      )}

      {validationResult && (
        <div className="validation-result">
          <div className={`result-card ${validationResult.isValid ? 'valid' : 'invalid'}`}>
            <div className="result-icon">
              {validationResult.isValid ? '✅' : '❌'}
            </div>

            <h3 className="result-message">{validationResult.message}</h3>

            {validationResult.ticket && (
              <div className="ticket-details">
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Bilet No:</span>
                    <span className="detail-value">{validationResult.ticket.id}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Etkinlik:</span>
                    <span className="detail-value">{validationResult.ticket.eventName}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Tarih:</span>
                    <span className="detail-value">
                      {new Date(validationResult.ticket.eventDate).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Salon:</span>
                    <span className="detail-value">{validationResult.ticket.venueName}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Koltuk:</span>
                    <span className="detail-value highlight">{validationResult.ticket.seatId}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Bölüm:</span>
                    <span className="detail-value">{validationResult.ticket.sectionName}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Müşteri:</span>
                    <span className="detail-value">{validationResult.ticket.customerName}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Bilet Tipi:</span>
                    <span className="detail-value">
                      {validationResult.ticket.ticketType === 0 && 'Tam Bilet'}
                      {validationResult.ticket.ticketType === 1 && 'Öğrenci'}
                      {validationResult.ticket.ticketType === 2 && 'Davetli'}
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Ücret:</span>
                    <span className="detail-value price">₺{validationResult.ticket.price.toFixed(2)}</span>
                  </div>

                  {validationResult.ticket.isUsed && (
                    <div className="detail-item full-width">
                      <span className="used-badge">
                        ⚠️ Kullanılmış ({new Date(validationResult.ticket.usedDate).toLocaleString('tr-TR')})
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="result-actions">
              {validationResult.isValid && !validationResult.ticket.isUsed && (
                <button onClick={handleUseTicket} className="use-ticket-btn">
                  ✓ Girişe İzin Ver (Bileti Kullan)
                </button>
              )}
              <button onClick={handleReset} className="scan-again-btn">
                🔄 Yeni Tarama Yap
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScanner;